import { BaseWebComponent } from '../components/BaseWebComponent.js'
import { basicDataReportsStore } from '../stores/BasicDataReportsStore.js'
import {
  configStore,
  CustomSideMenuPane,
  UIConfig,
} from '../stores/ConfigStore.js'
import {
  HomeLinks,
  ModalConfig,
  SelectedLayers,
  Source,
  UserInfo,
} from '../utils/types.js'
import { queryElement, queryElementAll } from '../utils/utils.js'
import { DataReportsMenu } from './DataReportsMenu.js'
import { HomeMenu } from './HomeMenu.js'
import { MapLayersMenu } from './MapLayersMenu.js'
import { RollUpReportsMenu } from './RollUpReportsMenu.js'

const template = `
<style>
  button {
    all: unset;
    outline: none;
    background: transparent;
    border: 1px solid transparent;
  }

  .cqmt-side-menu-container {
    display: flex;
    flex-direction: column;
    width: 4.25rem;
    height: 100%;
    background-color: var(--bunker-1000);
  }
  .cqmt-side-menu-item{
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    height: 4.25rem;
    width: 100%;
    border-bottom: 1px solid var(--bunker-900);
    color: var(--bunker-900);

    & .cqmt-side-menu-item-label {
      opacity: 0;
      translate: 0 50%;
      font-size: 0;
      overflow: hidden;
      transition:
        opacity 0.3s ease-in-out,
        translate 0.3s ease-in-out;
    }
  }
  .cqmt-side-menu-item:hover {
    color: white;
    background-color: var(--bunker-900);
    cursor: pointer;

    & .cqmt-side-menu-item-label {
      opacity: 1;
      translate: 0 0;

      padding: 2px 4px;

      text-align: center;
      font-family: 'Inter', sans-serif;
      font-weight: 500;
      font-size: 0.75rem;
      line-height: 1rem;
      letter-spacing: 0.5px;
    }
  }
  .cqmt-side-menu-icon {
    padding: 2px;
  }

  .cqmt-context-menu {
    min-width: 460px;
    display: none;
    height: 100%;
    background-color: #0D1420F2;
    color: white;
  }
  .cqmt-context-menu-container {
    padding: 8px 16px;
  }
  .cqmt-context-menu-header {
    display: flex;
    justify-content: space-between;

    width: 100%;
    padding: 16px 0px;

    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 0.75rem;
    line-height: 1rem;
    letter-spacing: 2px;
  }
  .close-icon {
    cursor: pointer;
  }
  .cqmt-context-menu-content {
    width: 100%;
    height: 100%;
  }
  .slot-container {
    display: none;
    width: 100%;
    height: 100%;
  }
  .slot-container.active {
    display: block;
  }
  .cqmt-side-menu-placeholder {
    position: relative;
    height: 4.25rem;
    width: 100%;
    overflow: hidden;
    border-bottom: 1px solid var(--bunker-900);
    background: var(--bunker-900);
  }
  .cqmt-side-menu-placeholder::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.08),
      transparent
    );
    animation: cqmt-side-menu-loading 1.2s infinite;
  }
  @keyframes cqmt-side-menu-loading {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(100%);
    }
  }
</style>

<div id="sideMenuContainer" class="cqmt-side-menu-container">
</div>

<div id="sideContextMenu" class="cqmt-context-menu">
  <div id="sideContextMenuContainer" class="cqmt-context-menu-container">
    <div id="contextMenuHeader" class="cqmt-context-menu-header">
      <span id="menuTitleText"></span>
      <button>
        <img id="menuCloseIcon" src="CQMT/assets/CloseIcon.svg" class="close-icon" />
      </button>
    </div>
    <div id="contextMenuContent" class="cqmt-context-menu-content">
    </div>
  </div>
</div>
`

export type SideMenuProps = {
  userInfo?: UserInfo | null
  homeLinks?: HomeLinks
  modalConfig?: ModalConfig | null
  sources?: Source[] | null
  selectedLayers?: SelectedLayers | null
}

type SideMenuElement = HTMLElement & SideMenuProps

class SideMenu extends BaseWebComponent implements SideMenuElement {
  private _userInfo: UserInfo
  private _sources: Source[]
  private _selectedLayers: SelectedLayers
  private _homeLinks: HomeLinks
  private _modalConfig: ModalConfig
  private openMenu: string
  private _subscriptions: (() => void)[]
  private isLoading: boolean
  private hasAttemptedInitialHomeOpen: boolean

  protected get template(): string {
    return template
  }

  constructor() {
    super()

    this.style.cssText = `
      display: inline-flex;
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      z-index: 20;
    `

    this.attachShadow({ mode: 'open' })
    this.shadowRoot.innerHTML = template

    this.handleMenuClose = this.handleMenuClose.bind(this)

    this._userInfo = { userName: '', userSubscription: '' }
    this._sources = []
    this._selectedLayers = {}
    this._homeLinks = { contactLink: '', supportLink: '' }
    this._modalConfig = { welcomeText: '', welcomeMessage: '', videoURL: '' }
    this.openMenu = ''
    this._subscriptions = []
    this.isLoading = !configStore.getState().isInitialized
    this.hasAttemptedInitialHomeOpen = false
  }

  /**
   * Displays any custom side menu items based on the provided UI configuration
   */
  private renderCustomMenuItems() {
    const { customSideMenuPanes } = configStore.getState().uiConfig

    const container = queryElement(
      this.shadowRoot,
      HTMLElement,
      '#sideMenuContainer',
    )

    const customItems = container.querySelectorAll('[data-custom-pane]')
    customItems.forEach((item) => item.remove())

    const sortedPanes = [...customSideMenuPanes].sort(
      (a, b) => (a.listOrder || 999) - (b.listOrder || 999),
    )

    sortedPanes.forEach((pane) => {
      const button = document.createElement('button')
      button.className = 'cqmt-side-menu-item'
      button.id = `sideMenu${pane.id}Button`
      button.setAttribute('data-custom-pane', 'true')

      button.innerHTML = `
      <div class="cqmt-side-menu-icon">
        <slot name="${pane.iconSlotName}"></slot>
      </div>
      <span class="cqmt-side-menu-item-label">
        ${pane.title.toUpperCase()}
      </span>
    `

      button.addEventListener('click', () => {
        this.handleCustomPaneClick(pane)
      })

      container.appendChild(button)
    })
  }

  private renderLoadingState(container: HTMLElement) {
    /**
     * The number of loading (shimmer) tabs to show on the sidemenu during loading
     */
    const numberOfShimmerTabs = 3
    for (let index = 0; index < numberOfShimmerTabs; index += 1) {
      const placeholder = document.createElement('div')
      placeholder.className = 'cqmt-side-menu-placeholder'
      container.appendChild(placeholder)
    }
  }

  /**
   * Displays the default side menu items (e.g. Home, Data Reports, Map Layers)
   * if they are enabled in the UI configuration.
   */
  private renderDefaultMenuItems() {
    const { uiConfig } = configStore.getState()

    const container = queryElement(
      this.shadowRoot,
      HTMLElement,
      '#sideMenuContainer',
    )

    if (uiConfig.defaultSideMenuTabVisibility.home) {
      let button = queryElement(
        this.shadowRoot,
        HTMLElement,
        '#sideMenuHomeButton',
      )
      if (!button) {
        button = document.createElement('button')
        button.className = 'cqmt-side-menu-item'
        button.id = 'sideMenuHomeButton'
        button.innerHTML = `
          <img src="CQMT/assets/HomeIcon.svg" class="cqmt-side-menu-icon" />
          <span class="cqmt-side-menu-item-label">
            HOME
          </span>
        `
        container.appendChild(button)
        this.addDefaultButtonEventListener('home')
      }
    } else {
      const button = queryElement(
        this.shadowRoot,
        HTMLElement,
        '#sideMenuHomeButton',
      )
      button?.remove()
    }

    if (uiConfig.defaultSideMenuTabVisibility['data-results']) {
      let button = queryElement(
        this.shadowRoot,
        HTMLElement,
        '#sideMenuDataResultsButton',
      )
      if (!button) {
        button = document.createElement('button')
        button.className = 'cqmt-side-menu-item'
        button.id = 'sideMenuDataResultsButton'
        button.innerHTML = `
        <img src="CQMT/assets/DataIcon.svg" class="cqmt-side-menu-icon" />
        <span class="cqmt-side-menu-item-label">
          DATA REPORTS
        </span>
      `
        container.appendChild(button)
        this.addDefaultButtonEventListener('data-results')
      }
    } else {
      const button = queryElement(
        this.shadowRoot,
        HTMLElement,
        '#sideMenuDataResultsButton',
      )
      button?.remove()
    }

    if (uiConfig.defaultSideMenuTabVisibility['map-layers']) {
      let button = queryElement(
        this.shadowRoot,
        HTMLElement,
        '#sideMenuLayersButton',
      )
      if (!button) {
        button = document.createElement('button')
        button.className = 'cqmt-side-menu-item'
        button.id = 'sideMenuLayersButton'
        button.innerHTML = `
        <img src="CQMT/assets/LayersIcon.svg" class="cqmt-side-menu-icon" />
        <span class="cqmt-side-menu-item-label">
          MAP LAYERS
        </span>
      `
        container.appendChild(button)
        this.addDefaultButtonEventListener('map-layers')
      }
    } else {
      const button = queryElement(
        this.shadowRoot,
        HTMLElement,
        '#sideMenuLayersButton',
      )
      button?.remove()
    }

    if (uiConfig.defaultSideMenuTabVisibility['roll-up-reports']) {
      let button = queryElement(
        this.shadowRoot,
        HTMLElement,
        '#sideMenuRollUpReportsButton',
      )
      if (!button) {
        button = document.createElement('button')
        button.className = 'cqmt-side-menu-item'
        button.id = 'sideMenuRollUpReportsButton'
        button.innerHTML = `
        <img src="CQMT/assets/calculator-icon.svg" class="cqmt-side-menu-icon" />
        <span class="cqmt-side-menu-item-label">
          ROLL-UP REPORTS
        </span>
      `
        container.appendChild(button)
        this.addDefaultButtonEventListener('roll-up-reports')
      }
    } else {
      const button = queryElement(
        this.shadowRoot,
        HTMLElement,
        '#sideMenuRollUpReportsButton',
      )
      button?.remove()
    }
  }

  /**
   * Creates a slot container element for custom content (defined in the UI configuration)
   * @param slotName - The name of the slot to create
   * @returns The created container element with the slot
   */
  private createSlotContainer(slotName: string): HTMLElement {
    const container = document.createElement('div')
    container.className = 'slot-container'
    container.id = `slot-${slotName}`

    const slot = document.createElement('slot')
    slot.name = slotName
    container.appendChild(slot)

    return container
  }

  /**
   * Shows a specific slot container and hides all others
   * @param slotName - The name of the slot to show
   */
  private showSlot(slotName: string) {
    const contextMenuContent = this.shadowRoot.querySelector(
      '#contextMenuContent',
    )

    // Hide all slot containers
    const allContainers = contextMenuContent.querySelectorAll('.slot-container')
    allContainers.forEach((container) => container.classList.remove('active'))

    // Show the specific slot container
    let targetContainer = contextMenuContent.querySelector(`#slot-${slotName}`)
    if (!targetContainer) {
      targetContainer = this.createSlotContainer(slotName)
      contextMenuContent.appendChild(targetContainer)
    }

    targetContainer.classList.add('active')
  }

  /**
   * Hides all slot containers by removing the active class
   */
  private hideAllSlots() {
    const contextMenuContent = this.shadowRoot.querySelector(
      '#contextMenuContent',
    )
    const allContainers = contextMenuContent.querySelectorAll('.slot-container')
    allContainers.forEach((container) => container.classList.remove('active'))
  }

  /**
   * Hides all content in the context menu (the pane that expands when a side menu item is clicked)
   */
  private hideAllContent() {
    // Hide all slots
    this.hideAllSlots()

    // Remove any dynamically created content (for default menus)
    const contextMenuContainer = this.shadowRoot.querySelector(
      '#sideContextMenuContainer',
    )

    // Find and remove any custom elements that were added (like cqmt-side-menu-home, etc.)
    const customElements = contextMenuContainer.querySelectorAll(
      'cqmt-side-menu-home, cqmt-side-menu-data-reports, cqmt-side-menu-map-layers, cqmt-side-menu-roll-up-reports',
    )
    customElements.forEach((element) => {
      contextMenuContainer.removeChild(element)
    })
  }

  /**
   * Handles clicks on custom side menu items (defined in the UI configuration)
   * @param pane - The custom side menu pane configuration
   */
  private handleCustomPaneClick(pane: CustomSideMenuPane) {
    const buttonId = `sideMenu${pane.id}Button`

    if (this.openMenu !== buttonId) {
      queryElement(
        this.shadowRoot,
        HTMLElement,
        '#sideContextMenu',
      ).style.display = 'block'
      queryElement(this.shadowRoot, HTMLElement, '#menuTitleText').innerText =
        pane.title.toUpperCase()

      if (this.openMenu !== '') {
        // eslint-disable-next-line unicorn/prefer-query-selector -- disabled when dynamically selecting elements
        this.shadowRoot.getElementById(`${this.openMenu}`).style.borderRight =
          'unset'
        // eslint-disable-next-line unicorn/prefer-query-selector -- disabled when dynamically selecting elements
        this.shadowRoot.getElementById(`${this.openMenu}`).style.paddingLeft =
          '0'

        this.hideAllContent()
      }

      queryElement(
        this.shadowRoot,
        HTMLElement,
        `#${buttonId}`,
      ).style.borderRight = '4px solid var(--patina-500)'
      queryElement(
        this.shadowRoot,
        HTMLElement,
        `#${buttonId}`,
      ).style.paddingLeft = '4px'

      // Show the specific slot for this pane
      this.showSlot(pane.contentSlotName)
      this.openMenu = buttonId
    } else {
      this.handleMenuClose()
    }
  }

  handleMenuClose() {
    if (this.openMenu !== '') {
      // eslint-disable-next-line unicorn/prefer-query-selector -- disabled when dynamically selecting elements
      this.shadowRoot.getElementById(`${this.openMenu}`).style.borderRight =
        'unset'
      // eslint-disable-next-line unicorn/prefer-query-selector -- disabled when dynamically selecting elements
      this.shadowRoot.getElementById(`${this.openMenu}`).style.paddingLeft = '0'

      this.hideAllContent()
    }

    queryElement(
      this.shadowRoot,
      HTMLElement,
      '#sideContextMenu',
    ).style.display = 'none'
    this.openMenu = ''
  }

  /**
   * Helper method that adds event listeners to the default side menu items
   * @param buttonId - The ID of the button to add event listener to
   */
  private addDefaultButtonEventListener(
    buttonId: keyof UIConfig['defaultSideMenuTabVisibility'],
  ) {
    switch (buttonId) {
      case 'home':
        this.shadowRoot
          .querySelector('#sideMenuHomeButton')
          .addEventListener('click', () => {
            if (this.openMenu !== 'sideMenuHomeButton') {
              queryElement(
                this.shadowRoot,
                HTMLElement,
                '#sideContextMenu',
              ).style.display = 'block'
              queryElement(
                this.shadowRoot,
                HTMLElement,
                '#menuTitleText',
              ).innerText = 'HOME'

              const contextMenuContainer = this.shadowRoot.querySelector(
                '#sideContextMenuContainer',
              )

              if (this.openMenu !== '') {
                // eslint-disable-next-line unicorn/prefer-query-selector -- disabled when dynamically selecting elements
                this.shadowRoot.getElementById(
                  `${this.openMenu}`,
                ).style.borderRight = 'unset'
                // eslint-disable-next-line unicorn/prefer-query-selector -- disabled when dynamically selecting elements
                this.shadowRoot.getElementById(
                  `${this.openMenu}`,
                ).style.paddingLeft = '0'
                this.hideAllContent()
              }
              queryElement(
                this.shadowRoot,
                HTMLElement,
                '#sideMenuHomeButton',
              ).style.borderRight = '4px solid var(--patina-500)'
              queryElement(
                this.shadowRoot,
                HTMLElement,
                '#sideMenuHomeButton',
              ).style.paddingLeft = '4px'

              const homeMenu = document.createElement(
                'cqmt-side-menu-home',
              ) as HomeMenu
              homeMenu.userinfo = this.userInfo
              homeMenu.sources = this.sources
              homeMenu.homeLinks = this.homeLinks
              homeMenu.modalConfig = this.modalConfig
              contextMenuContainer.appendChild(homeMenu)
              this.openMenu = 'sideMenuHomeButton'
            } else {
              this.handleMenuClose()
            }
          })
        break
      case 'data-results':
        this.shadowRoot
          .querySelector('#sideMenuDataResultsButton')
          .addEventListener('click', () => {
            if (this.openMenu !== 'sideMenuDataResultsButton') {
              queryElement(
                this.shadowRoot,
                HTMLElement,
                '#sideContextMenu',
              ).style.display = 'block'
              queryElement(
                this.shadowRoot,
                HTMLElement,
                '#menuTitleText',
              ).innerText = 'DATA REPORTS'

              const contextMenuContainer = this.shadowRoot.querySelector(
                '#sideContextMenuContainer',
              )

              if (this.openMenu !== '') {
                // eslint-disable-next-line unicorn/prefer-query-selector -- disabled when dynamically selecting elements
                this.shadowRoot.getElementById(
                  `${this.openMenu}`,
                ).style.borderRight = 'unset'
                // eslint-disable-next-line unicorn/prefer-query-selector -- disabled when dynamically selecting elements
                this.shadowRoot.getElementById(
                  `${this.openMenu}`,
                ).style.paddingLeft = '0'
                this.hideAllContent()
              }
              queryElement(
                this.shadowRoot,
                HTMLElement,
                '#sideMenuDataResultsButton',
              ).style.borderRight = '4px solid var(--patina-500)'
              queryElement(
                this.shadowRoot,
                HTMLElement,
                '#sideMenuDataResultsButton',
              ).style.paddingLeft = '4px'

              const dataReportsMenu = document.createElement(
                'cqmt-side-menu-data-reports',
              ) as DataReportsMenu
              contextMenuContainer.appendChild(dataReportsMenu)

              this.openMenu = 'sideMenuDataResultsButton'
            } else {
              basicDataReportsStore.getState().setActiveTab(undefined)
              this.handleMenuClose()
            }
          })
        break
      case 'map-layers':
        this.shadowRoot
          .querySelector('#sideMenuLayersButton')
          .addEventListener('click', () => {
            if (this.openMenu !== 'sideMenuLayersButton') {
              queryElement(
                this.shadowRoot,
                HTMLElement,
                '#sideContextMenu',
              ).style.display = 'block'
              queryElement(
                this.shadowRoot,
                HTMLElement,
                '#menuTitleText',
              ).innerText = 'MAP LAYERS'

              const contextMenuContainer = this.shadowRoot.querySelector(
                '#sideContextMenuContainer',
              )

              if (this.openMenu !== '') {
                // eslint-disable-next-line unicorn/prefer-query-selector -- disabled when dynamically selecting elements
                this.shadowRoot.getElementById(
                  `${this.openMenu}`,
                ).style.borderRight = 'unset'
                // eslint-disable-next-line unicorn/prefer-query-selector -- disabled when dynamically selecting elements
                this.shadowRoot.getElementById(
                  `${this.openMenu}`,
                ).style.paddingLeft = '0'
                this.hideAllContent()
              }
              queryElement(
                this.shadowRoot,
                HTMLElement,
                '#sideMenuLayersButton',
              ).style.borderRight = '4px solid var(--patina-500)'
              queryElement(
                this.shadowRoot,
                HTMLElement,
                '#sideMenuLayersButton',
              ).style.paddingLeft = '4px'

              const mapLayersMenu = document.createElement(
                'cqmt-side-menu-map-layers',
              ) as MapLayersMenu
              mapLayersMenu.selectedLayers = this.selectedLayers
              mapLayersMenu.sources = this.sources
              contextMenuContainer.appendChild(mapLayersMenu)
              this.openMenu = 'sideMenuLayersButton'
            } else {
              this.handleMenuClose()
            }
          })
        break
      case 'roll-up-reports':
        this.shadowRoot
          .querySelector('#sideMenuRollUpReportsButton')
          .addEventListener('click', () => {
            if (this.openMenu !== 'sideMenuRollUpReportsButton') {
              queryElement(
                this.shadowRoot,
                HTMLElement,
                '#sideContextMenu',
              ).style.display = 'block'
              queryElement(
                this.shadowRoot,
                HTMLElement,
                '#menuTitleText',
              ).innerText = 'ROLL-UP REPORTS'

              const contextMenuContainer = this.shadowRoot.querySelector(
                '#sideContextMenuContainer',
              )

              if (this.openMenu !== '') {
                // eslint-disable-next-line unicorn/prefer-query-selector -- disabled when dynamically selecting elements
                this.shadowRoot.getElementById(
                  `${this.openMenu}`,
                ).style.borderRight = 'unset'
                // eslint-disable-next-line unicorn/prefer-query-selector -- disabled when dynamically selecting elements
                this.shadowRoot.getElementById(
                  `${this.openMenu}`,
                ).style.paddingLeft = '0'
                this.hideAllContent()
              }
              queryElement(
                this.shadowRoot,
                HTMLElement,
                '#sideMenuRollUpReportsButton',
              ).style.borderRight = '4px solid var(--patina-500)'
              queryElement(
                this.shadowRoot,
                HTMLElement,
                '#sideMenuRollUpReportsButton',
              ).style.paddingLeft = '4px'

              const rollUpReportsMenu = document.createElement(
                'cqmt-side-menu-roll-up-reports',
              ) as RollUpReportsMenu
              contextMenuContainer.appendChild(rollUpReportsMenu)
              this.openMenu = 'sideMenuRollUpReportsButton'
            } else {
              this.handleMenuClose()
            }
          })
        break
    }
  }

  private attachPaneToggleEventListeners() {
    queryElementAll(
      this.shadowRoot,
      HTMLElement,
      '.cqmt-side-menu-item',
    ).forEach((element) => {
      element.addEventListener('click', () => {
        const paneToggleEvent = new CustomEvent('cqmt-side-menu-pane-toggle', {
          detail: { pane: this.openMenu },
          bubbles: true,
        })

        this.dispatchEvent(paneToggleEvent)
      })
    })
  }

  private attemptOpenHomeMenuByDefault() {
    if (this.hasAttemptedInitialHomeOpen) {
      return
    }

    const openHomeMenuByDefaultPref = localStorage.getItem(
      'openHomeMenuByDefault',
    )

    // On first visit the home menu should be open by default.
    if (openHomeMenuByDefaultPref === 'undefined') {
      localStorage.setItem('openHomeMenuByDefault', 'true')
    }
    if (openHomeMenuByDefaultPref === 'true') {
      setTimeout(() =>
        queryElement(
          this.shadowRoot,
          HTMLElement,
          '#sideMenuHomeButton',
        )?.click(),
      )
    }

    this.hasAttemptedInitialHomeOpen = true
  }

  private renderSideMenu() {
    const container = queryElement(
      this.shadowRoot,
      HTMLElement,
      '#sideMenuContainer',
    )
    container.replaceChildren()

    this.isLoading = !configStore.getState().isInitialized

    if (this.isLoading) {
      this.renderLoadingState(container)
      return
    }

    this.renderDefaultMenuItems()
    this.renderCustomMenuItems()
    this.attachPaneToggleEventListeners()
    this.attemptOpenHomeMenuByDefault()
  }

  connectedCallback() {
    this.renderSideMenu()

    const unsubscribeFromUIConfigUpdates = configStore.subscribe(
      (state) => ({
        uiConfig: state.uiConfig,
        isInitialized: state.isInitialized,
      }),
      () => {
        this.renderSideMenu()
      },
    )
    this._subscriptions.push(unsubscribeFromUIConfigUpdates)

    this.shadowRoot
      .querySelector('#menuCloseIcon')
      .addEventListener('click', this.handleMenuClose)

    /*
      If the modal is ever opened with an empty configuration then set the 
      configuration from this component (if it exists) and show the modal.
    */
    this.shadowRoot.addEventListener('cqmt-open-modal', () => {
      if (this.isEmpty(this.modalConfig)) {
        return
      }
      const homeMenu = this.shadowRoot.querySelector(
        'cqmt-side-menu-home',
      ) as HomeMenu
      homeMenu.modalConfig = this.modalConfig
      const dialog = queryElement(
        homeMenu.shadowRoot,
        HTMLDialogElement,
        '#cqmtWelcomeDialog',
      )
      dialog.showModal()
    })
  }

  disconnectedCallback() {
    this._subscriptions.forEach((unsubscribe) => unsubscribe())
  }

  static get observedAttributes() {
    return ['userinfo', 'sources', 'selectedlayers', 'homelinks', 'modalconfig']
  }

  get userInfo(): UserInfo {
    return this._userInfo
  }
  set userInfo(newVal: UserInfo) {
    this._userInfo = newVal
  }

  get sources(): Source[] {
    return this._sources
  }
  set sources(newVal: Source[]) {
    this._sources = newVal
  }

  get selectedLayers(): SelectedLayers {
    return this._selectedLayers
  }
  set selectedLayers(newVal: SelectedLayers) {
    this._selectedLayers = newVal
  }

  get homeLinks(): HomeLinks {
    return this._homeLinks
  }
  set homeLinks(newVal: HomeLinks) {
    this._homeLinks = newVal
  }

  get modalConfig(): ModalConfig {
    return this._modalConfig
  }
  set modalConfig(newVal: ModalConfig) {
    this._modalConfig = newVal
  }

  attributeChangedCallback(attrName, oldVal, newVal) {
    if (attrName.toLowerCase() === 'userinfo') {
      this.userInfo = newVal
    }

    if (attrName.toLowerCase() === 'sources') {
      this.sources = newVal
    }

    if (attrName.toLowerCase() === 'homelinks') {
      this.homeLinks = newVal
    }

    if (attrName.toLowerCase() === 'modalconfig') {
      this.modalConfig = newVal
    }
  }

  isEmpty(obj) {
    return Object.keys(obj).length === 0 && obj.constructor === Object
  }
}

if (!window.customElements.get('cqmt-side-menu')) {
  window.customElements.define('cqmt-side-menu', SideMenu)
}
